/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from "react";
import { DocumentContainer } from "containers/document/DocumentContainer";
import { getCurrActiveElemNum } from "selectors/accessibilitySelectors";
import { ContextMenuCanvasContainer } from "containers/contextMenu/ContextMenuCanvasContainer";
import { PrintHelperContainer } from "print/components/PrintHelperContainer";
import { apiFactory } from "api/external/ApiFactory";
import { apiNames } from "constants/apiNames";
import { LANDMARK_NAV_DIR, LANDMARK_LIST } from "constants/constants";
import PreviewCss from "./Preview.css";
import * as stores from "stores";
import { isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import { SkipNavContainer } from "containers/SkipNavContainer";
import { isInputOrEditableDiv } from "utils/DomUtils";
import { A, F6 } from "dc-preview-utils/src/Constants/key-codes";
import { isControlORCommandPressed } from "dc-preview-utils/src/Utils/EventHandlingUtils";
import { AriaLiveContainer } from "containers/aria/AriaLiveContainer";
import GetAppContainer from "containers/getApp/GetAppContainer";
import uuid from "uuid";
import { Flex } from "@react-spectrum/layout";
import TopPanelContainer from "containers/topPanel/TopPanelContainer";
import FloatingWindowContainer from "containers/FloatingWindow/FloatingWindowContainer";
import NotificationViewContainer from "containers/notificationView/NotificationViewContainer";

export class Preview extends Component {
  constructor(props) {
    super(props);
    if (!window.docSessionId) {
      this.previewScopedSessionId = true;
      window.docSessionId = uuid.v4();
    }
    this.onKeydown = this.onKeydown.bind(this);
    document.body.addEventListener("keydown", this.onKeydown);
    this.lhpView = null;
    this.hudView = null;
    this.documentView = null;
    this.setLHPView = this.setLHPView.bind(this);
    this.setHUDView = this.setHUDView.bind(this);
    this.setDocumentView = this.setDocumentView.bind(this);
    this._registerApis();
    this.state = {
      lhpLoaded: false
    };

    this.lhpContainer = null;
    this.isMobile = isCurrentDeviceMobile();
  }

  _registerApis() {
    apiFactory.registerApi(apiNames.PREVIEW, () => ({
      getDimensions: () => this.getPreviewDimensions(),
      modifyDimensions: ({ width, height }) => this._modifyDimensions({ width, height })
    }));
  }

  loadLHPChunk() {
    if (this.props.showLHP && !this.state.lhpLoaded) {
      import("containers/lhp/LHPContainer").then(LHPContainer => {
        this.lhpContainer = LHPContainer.LHPContainer;
        this.setState(() => ({
          lhpLoaded: true
        }));
      });
    }
  }

  componentDidMount() {
    this.loadLHPChunk();
  }

  componentDidUpdate() {
    this.loadLHPChunk();
  }

  componentWillUnmount() {
    document.body.removeEventListener("keydown", this.onKeydown);
    if (this.previewScopedSessionId) {
      window.docSessionId = undefined;
    }

    // Cleanup floating windows to prevent memory leaks
    const floatingWindowsManager = require("FloatingWindow/FloatingWindowsManager").default;
    if (floatingWindowsManager) {
      floatingWindowsManager.cleanup();
    }
  }

  _modifyDimensions({ width, height }) {
    this.props.setDimensions({ width, height });
  }

  onKeydown(event) {
    switch (event.keyCode) {
      case F6:
        const { shouldUseModernViewer } = this.props;
        if (shouldUseModernViewer) {
          this.switchToNextModernViewerSection(
            event,
            event.shiftKey ? LANDMARK_NAV_DIR.BACKWARD : LANDMARK_NAV_DIR.FORWARD
          );
        }
        break;
      case A:
        if (isControlORCommandPressed(event) && !isInputOrEditableDiv(document.activeElement)) {
          event.preventDefault();
        }
        break;
      default:
        break;
    }
  }

  switchToNextModernViewerSection(event, direction = LANDMARK_NAV_DIR.FORWARD) {
    const currActiveElemNum = getCurrActiveElemNum(stores);
    let elemNumToBeActive = null;
    if (currActiveElemNum === -1) {
      if (direction === LANDMARK_NAV_DIR.FORWARD) {
        elemNumToBeActive = 0;
      } else {
        elemNumToBeActive = LANDMARK_LIST.length - 1;
      }
    } else {
      if (direction === LANDMARK_NAV_DIR.FORWARD) {
        elemNumToBeActive = (currActiveElemNum + 1) % LANDMARK_LIST.length;
      } else {
        elemNumToBeActive = (currActiveElemNum - 1 + LANDMARK_LIST.length) % LANDMARK_LIST.length;
      }
    }
    const elementIdToBeActive = LANDMARK_LIST[elemNumToBeActive];
    let componentToBeActive = null;

    if (elementIdToBeActive !== "") {
      // empty id is for global-nav, focussing will be redirected to FocusRegionProvider for global-nav
      event.preventDefault();
      componentToBeActive = document.getElementById(elementIdToBeActive);
      if (componentToBeActive) {
        const rect = componentToBeActive.getBoundingClientRect();
        componentToBeActive = rect.height === 0 || rect.width === 0 ? null : componentToBeActive; // element might be present in dismissed state
      }
    }

    /**
     * setTimeout is used as a work-around because various
     * the accessible sections in the DOM often flicker and lose focus
     * on key presses.
     *
     * It is TEMPORARY - until the flickering is fixed for all components
     */
    if (this.accessibilityTimer) {
      clearTimeout(this.accessibilityTimer);
      this.accessibilityTimer = null;
    }

    stores.accessibilityStore.setCurrActiveElemNum(elemNumToBeActive);

    if (componentToBeActive) {
      if (componentToBeActive.getAttribute("tabIndex") !== "-1") {
        // setting and unsetting tabIndex programatically for Google YOLO, as it's not set on GoogleYOLO component explicitly
        componentToBeActive.addEventListener("blur", this.handleBlur);
        componentToBeActive.setAttribute("tabIndex", "-1");
        if (elementIdToBeActive === "pageview-current-page") {
          componentToBeActive.classList.add(PreviewCss.pageView);
        }
      }

      /** to skip event being listened by FocusRegionProvider
       * event propagation is stopped, but this must be done only
       * for elements which we are going to focus ourselves, otherwise due to recursive call,
       * incorrect event propagation can also be stopped
       */
      event.stopPropagation();

      this.accessibilityTimer = setTimeout(() => {
        componentToBeActive.focus();
      }, 500);
    } else if (elementIdToBeActive !== "") {
      this.switchToNextModernViewerSection(event, direction);
    }
  }

  handleBlur = event => {
    const { target } = event;
    if (target) {
      target.removeEventListener("blur", this.handleBlur);
      target.removeAttribute("tabindex");
      if (target.classList.contains(PreviewCss.pageView)) {
        target.classList.remove(PreviewCss.pageView);
      }
    }
  };

  setLHPView(view) {
    this.lhpView = view;
  }

  setHUDView(view) {
    this.hudView = view;
  }

  setDocumentView(view) {
    this.documentView = view;
  }

  getPreviewDimensions = () => ({
    height: this.elementHandle.clientHeight,
    width: this.elementHandle.clientWidth
  });

  render() {
    let docViewBgColor;
    docViewBgColor = this.props.config.docViewBgColor;
    let width = this.props.width;
    const style = { width };
    const { formatMessage } = this.props.intl;
    return (
      <div
        className={PreviewCss.Preview}
        ref={handle => {
          this.elementHandle = handle;
        }}
        style={style}
      >
        <ContextMenuCanvasContainer />
        <FloatingWindowContainer />
        {this.isMobile ? <SkipNavContainer intl={this.props.intl} /> : null}
        {this.props.showLHP && this.state.lhpLoaded && this.props.isPreviewable ? (
          <this.lhpContainer setLHPView={this.setLHPView} />
        ) : null}
        <Flex direction="column" UNSAFE_className={PreviewCss.documentViewWrapper}>
          <TopPanelContainer getPreviewDimensions={this.getPreviewDimensions} />
          <NotificationViewContainer {...this.props} />
          <DocumentContainer
            setDocumentView={this.setDocumentView}
            docViewBgColor={docViewBgColor}
            setHUDView={this.setHUDView}
            intl={this.props.intl}
            showQuickToolsInAppConfig={this.props.appConfig.showQuickTools}
            isPreviewable={this.props.isPreviewable}
          />
        </Flex>
        <PrintHelperContainer />
        <AriaLiveContainer />
        <GetAppContainer
          intl={this.props.intl}
          title={formatMessage({ id: "mobile.mv.getapp.heading" })}
          subHeading={formatMessage({ id: "mobile.mv.getapp.subheading" })}
          description={formatMessage({ id: "mobile.mv.getapp.description" })}
        />
      </div>
    );
  }
}
